import React from 'react';

/**
 * Plays a voice's sample audio. A single `Audio` object rather than an element
 * per row, so starting one preview inherently stops the previous one.
 */
export function useVoicePreview() {
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const [playingId, setPlayingId] = React.useState<string | null>(null);

    const stop = React.useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
        }
        setPlayingId(null);
    }, []);

    const toggle = React.useCallback(
        (id: string, previewUrl: string | null | undefined) => {
            if (!previewUrl) {
                return;
            }
            if (playingId === id) {
                stop();
                return;
            }

            audioRef.current?.pause();

            const audio = new Audio(previewUrl);
            // So the button doesn't sit on "stop" after the sample finishes.
            audio.onended = () => setPlayingId(null);
            audioRef.current = audio;

            void audio.play().catch((error: DOMException) => {
                // AbortError means a pause() or new src interrupted playback
                // (switching voices, stopping), not a load failure.
                if (error?.name !== 'AbortError') {
                    setPlayingId((current) => (current === id ? null : current));
                }
            });
            setPlayingId(id);
        },
        [playingId, stop],
    );

    // A preview left playing would talk over whatever the user does next.
    React.useEffect(() => {
        return () => {
            audioRef.current?.pause();
            audioRef.current = null;
        };
    }, []);

    return { playingId, toggle, stop };
}
